import { useState } from 'react';
import { Link } from '@common/Link/Link';
import { Modal } from '@common/Modal/Modal';
import { useTranslation } from 'next-i18next';
import { Button, TYPE } from '@common/Button/Button';
import { IconID } from '@common/Icon/Icon';
import { DEFAULT_CURRENCY } from '@libs/constants';
import {
  PriceQuantityUnit,
  calculateCopperSurchargePerM,
  calculateLengthUnitPrice,
  formatPrice,
} from '@libs/product-price-utils';
import type { CopperPrice } from '@models/graphql';
import { GraphQlPriceType } from '@models/graphql';
import classNames from 'classnames';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { ProductAttributeValueAmountUnit } from '@libs/attribute.helper';
import { getCuttingCostTranslationKeyByPriceType } from '@libs/buy-box';
import type { UnitCode } from '@icommerce/units';
import { LengthCode, WeightPerLengthCode, getUnitLabelForUnitSystem } from '@icommerce/units';

export interface QuantityDiscountProps {
  sku?: string;
  unitLabel?: string;
  copperIndex?: ProductAttributeValueAmountUnit;
  dailyCopperPriceDefinition?: CopperPrice[];
  showCopperSurchargeInfo: boolean;
  showCuttingCostInfo: boolean;
}

interface DiscountRowProps {
  pieceCentAmount: number;
  quantity: number;
  quantityUnitLabel: string | undefined;
  lengthCentAmount?: number;
  lengthUnitCode?: UnitCode;
}

interface CopperPriceData {
  productIndex: {
    weightPerLength: number;
    weightPerLengthUnit: string;
  };
  dailyPrice: {
    pricePerWeightCurrency: string;
    pricePerWeight: number;
    weight: number;
    weightUnit: string;
  };
}

export const DiscountRow = ({
  pieceCentAmount,
  quantity,
  quantityUnitLabel,
  lengthCentAmount,
  lengthUnitCode,
}: DiscountRowProps) => {
  const { t, i18n } = useTranslation('product/buyBox');
  const priceData = useProductPageStore(selector => selector.priceData);
  const unitSystem = useProductPageStore(selector => selector.unitSystem);
  return (
    <tr>
      <td className="border px-4 py-2">
        {quantity} {quantityUnitLabel}
      </td>
      <td className="border px-4 py-2">
        {priceData.priceType !== GraphQlPriceType.LengthAndPieceWithoutCopper && (
          <>
            {pieceCentAmount && pieceCentAmount > 0
              ? `${formatPrice(pieceCentAmount, i18n.language, priceData.currencyCode)}/${quantityUnitLabel || ''}`
              : t('BUY_BOX.PRICE_ON_REQUEST')}
            {lengthCentAmount && lengthCentAmount > 0
              ? ` + ${formatPrice(lengthCentAmount, i18n.language, priceData.currencyCode)}/${
                  getUnitLabelForUnitSystem(lengthUnitCode, unitSystem) || ''
                }`
              : ''}
          </>
        )}
        {priceData.priceType === GraphQlPriceType.LengthAndPieceWithoutCopper &&
          // TODO database values should be used for lengthUnit
          (calculateLengthUnitPrice(pieceCentAmount, lengthCentAmount || 0, 100) > 0
            ? t('BUY_BOX.DISCOUNT_LENGTH_AND_PIECE_WITHOUT_COPPER', {
                price: formatPrice(
                  calculateLengthUnitPrice(pieceCentAmount, lengthCentAmount || 0, 100),
                  i18n.language,
                  priceData.currencyCode
                ),
                lengthAmount: 100,
                lengthUnit: getUnitLabelForUnitSystem(LengthCode.MILLIMETER, unitSystem),
              })
            : t('BUY_BOX.PRICE_ON_REQUEST'))}
      </td>
    </tr>
  );
};

export const QuantityDiscount = ({
  showCopperSurchargeInfo,
  showCuttingCostInfo,
  copperIndex,
  dailyCopperPriceDefinition,
  sku,
  unitLabel,
}: QuantityDiscountProps) => {
  const [overlay, setOverlay] = useState(false);
  const handleOpen = () => setOverlay(true);
  const { t } = useTranslation(['product/buyBox', 'layout/general']);
  const priceData = useProductPageStore(selector => selector.priceData);

  let copperPriceData: CopperPriceData | undefined;
  const copperPrice = dailyCopperPriceDefinition?.find(price => price.currency_code === priceData.currencyCode);

  if (
    priceData.currencyCode === DEFAULT_CURRENCY &&
    copperIndex &&
    copperIndex.amount &&
    copperIndex.unit === WeightPerLengthCode.KILOGRAM_PER_KILOMETER &&
    copperPrice &&
    copperPrice.price &&
    copperPrice.unit_multiplier &&
    copperPrice.unit === PriceQuantityUnit.KILOGRAM
  ) {
    copperPriceData = {
      productIndex: {
        weightPerLength: copperIndex.amount,
        weightPerLengthUnit: copperIndex.unit,
      },
      dailyPrice: {
        pricePerWeight: parseFloat(copperPrice.price.toFixed(2)),
        pricePerWeightCurrency: copperPrice.currency_code,
        weight: copperPrice.unit_multiplier,
        weightUnit: copperPrice.unit,
      },
    };
  }

  const calculateCopperSurcharge = (): string | number =>
    copperPriceData
      ? calculateCopperSurchargePerM(
          copperPriceData.productIndex.weightPerLength,
          copperPriceData.dailyPrice.pricePerWeight,
          copperPriceData.dailyPrice.weight
        ) || '-'
      : '-';

  const quantityLevels = priceData.discountQuantity?.reduce((acc, curr) => {
    if (
      curr.discount_threshold !== null &&
      curr.discount_threshold !== undefined &&
      acc.indexOf(curr.discount_threshold) < 0
    ) {
      acc.push(curr.discount_threshold);
    }
    return acc;
  }, [] as Array<number>);

  // merge back multiple quantity discount items to a single item if they belong to the same price tier
  // (e.g. for pricetype LengthAndPiece which contains two items for each price tier)
  const mergedQuantityDiscounts = quantityLevels
    ?.map<{
      discountThreshold: number;
      pieceCentAmount?: number | null;
      lengthCentAmount?: number | null;
      currencyCode: string;
    }>(quantityLevel => {
      // currently we can only identify the price for each type by its unit, this means the code currently only works for length unit meter
      // as soon as we introduce other units, we need to change this code
      // TODO database values should be used for units
      switch (priceData.priceType) {
        case GraphQlPriceType.LengthAndPiece:
        case GraphQlPriceType.LengthAndPieceWithoutCopper:
          return {
            discountThreshold: quantityLevel,
            pieceCentAmount: priceData.discountQuantity?.find(
              item => item.discount_threshold === quantityLevel && item.unit === PriceQuantityUnit.PIECE
            )?.price,
            lengthCentAmount: priceData.discountQuantity?.find(
              item => item.discount_threshold === quantityLevel && item.unit !== PriceQuantityUnit.PIECE
            )?.price,
            currencyCode: priceData.currencyCode,
          };
        case GraphQlPriceType.LengthWithoutCopper:
        case GraphQlPriceType.Length:
        case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
        case GraphQlPriceType.TribotapeLength:
          return {
            discountThreshold: quantityLevel,
            lengthCentAmount: priceData.discountQuantity?.find(item => item.discount_threshold === quantityLevel)
              ?.price,
            currencyCode: priceData.currencyCode,
          };
        case GraphQlPriceType.PieceWithCopper:
        case GraphQlPriceType.Piece:
        case GraphQlPriceType.PieceWithChainLengthDependency:
          return {
            discountThreshold: quantityLevel,
            pieceCentAmount: priceData.discountQuantity?.find(
              item => item.discount_threshold === quantityLevel && item.unit === PriceQuantityUnit.PIECE
            )?.price,
            currencyCode: priceData.currencyCode,
          };
        case GraphQlPriceType.LeadScrewLength:
          return {
            discountThreshold: quantityLevel,
            pieceCentAmount: priceData.discountQuantity?.find(
              item => item.discount_threshold === quantityLevel && item.unit === PriceQuantityUnit.MILLIMETER
            )?.price,
            currencyCode: priceData.currencyCode,
          };
        case GraphQlPriceType.ChainLink:
          return {
            discountThreshold: quantityLevel,
            pieceCentAmount: priceData.discountQuantity?.find(
              item => item.discount_threshold === quantityLevel && item.unit === PriceQuantityUnit.LINK
            )?.price,
            currencyCode: priceData.currencyCode,
          };
        case GraphQlPriceType.ChainLength:
          return {
            discountThreshold: quantityLevel,
            lengthCentAmount: priceData.discountQuantity?.find(item => item.discount_threshold === quantityLevel)
              ?.price,
            currencyCode: priceData.currencyCode,
          };
      }
    })
    .sort((q1, q2) => q1.discountThreshold - q2.discountThreshold);

  const cuttingCostTranslationKey = getCuttingCostTranslationKeyByPriceType(priceData.priceType);

  return (
    <div className="mt-4">
      <Link
        className="text-2xl text-gray-500"
        icon={IconID.ARROW_RIGHT}
        text={t('BUY_BOX.QUANTITY_DISCOUNT')}
        bigIcon={false}
        onClick={handleOpen}
      />
      <Modal
        visible={overlay}
        onClose={() => setOverlay(false)}
        headline={`${t('BUY_BOX.SCALE_PRICES_TITLE')} ${sku || ''}`}
      >
        <div data-testid="quantity-discount-overlay" className="max-w-4xl w-full">
          <table className="w-full font-light">
            <tbody className="bg-gray-50 border-gray-200 font-light text-xs">
              {mergedQuantityDiscounts?.map((row, i) => {
                switch (priceData.priceType) {
                  case GraphQlPriceType.LengthWithoutCopper:
                  case GraphQlPriceType.Length:
                  case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
                  case GraphQlPriceType.TribotapeLength:
                  case GraphQlPriceType.ChainLength:
                    return (
                      <DiscountRow
                        pieceCentAmount={row.lengthCentAmount || -1}
                        quantity={row.discountThreshold}
                        quantityUnitLabel={unitLabel}
                        /* eslint-disable-next-line react/no-array-index-key */
                        key={`$priceTier-${i}`}
                      />
                    );
                  case GraphQlPriceType.LeadScrewLength:
                  case GraphQlPriceType.Piece:
                  case GraphQlPriceType.PieceWithCopper:
                  case GraphQlPriceType.PieceWithChainLengthDependency:
                  case GraphQlPriceType.ChainLink:
                    return (
                      <DiscountRow
                        pieceCentAmount={row.pieceCentAmount || -1}
                        quantity={row.discountThreshold}
                        quantityUnitLabel={unitLabel}
                        /* eslint-disable-next-line react/no-array-index-key */
                        key={`$priceTier-${i}`}
                      />
                    );
                  case GraphQlPriceType.LengthAndPiece:
                    return (
                      <DiscountRow
                        pieceCentAmount={row.pieceCentAmount || -1}
                        quantity={row.discountThreshold}
                        quantityUnitLabel={unitLabel}
                        /* eslint-disable-next-line react/no-array-index-key */
                        key={`$priceTier-${i}`}
                        lengthCentAmount={row.lengthCentAmount || -1}
                        lengthUnitCode={LengthCode.METER}
                      />
                    );
                  case GraphQlPriceType.LengthAndPieceWithoutCopper:
                    return (
                      <DiscountRow
                        pieceCentAmount={row.pieceCentAmount || -1}
                        quantity={row.discountThreshold}
                        quantityUnitLabel={unitLabel}
                        /* eslint-disable-next-line react/no-array-index-key */
                        key={`$priceTier-${i}`}
                        lengthCentAmount={row.lengthCentAmount ? row.lengthCentAmount / 100 : -1} // TODO database values should be used!
                        lengthUnitCode={LengthCode.MILLIMETER}
                      />
                    );
                }
              })}
            </tbody>
          </table>
          {showCopperSurchargeInfo && (
            <table className="w-full font-light">
              <tbody
                className={classNames(
                  'border',
                  'border-t-0',
                  showCuttingCostInfo ? 'border-b-0' : '',
                  'bg-gray-50',
                  'border-gray-200',
                  'font-light',
                  'text-xs'
                )}
              >
                <tr>
                  <td className="px-4 py-2">{t('BUY_BOX.PRICE_PLUS_COPPER_INFO_SHORTCUT')}</td>
                </tr>
              </tbody>
            </table>
          )}
          {showCuttingCostInfo && (
            <table className="w-full font-light">
              <tbody
                className={classNames(
                  'bg-gray-50',
                  'border-gray-200',
                  'font-light',
                  'text-xs',
                  showCopperSurchargeInfo ? '' : 'border-t-0'
                )}
              >
                <tr>
                  <td className="border px-4 py-2 col-span-2">{t(cuttingCostTranslationKey)}</td>
                </tr>
              </tbody>
            </table>
          )}
          <div className="my-8 w-1/2">
            <Button
              text={t('GENERAL.CLOSE', { ns: 'layout/general' })}
              type={TYPE.SECONDARY}
              onClick={() => setOverlay(false)}
            />
          </div>
          {showCopperSurchargeInfo && (
            <>
              <p className="mb-4 text-sm font-light">{t('BUY_BOX.PRICE_PLUS_COPPER_INFO')}</p>
              <p className="mb-4 text-sm font-light">
                {t('BUY_BOX.COPPER_DAILY_QUOTATION_INFO', {
                  price: copperPriceData ? copperPriceData.dailyPrice.pricePerWeight : '-',
                  currency: copperPriceData ? copperPriceData.dailyPrice.pricePerWeightCurrency : '-',
                  weight: copperPriceData ? copperPriceData.dailyPrice.weight : '-',
                  weightUnit: copperPriceData ? copperPriceData.dailyPrice.weightUnit : '-',
                })}
              </p>
              <p className="mb-4 text-sm font-light">
                {t('BUY_BOX.COPPER_SURCHARGE_INFO', {
                  price: calculateCopperSurcharge(),
                  currency: copperPriceData ? copperPriceData.dailyPrice.pricePerWeightCurrency : '-',
                })}
              </p>
              <p className="text-sm font-light">{t('BUY_BOX.COPPER_DISCLAIMER')}</p>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};
